import { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from '@radix-ui/react-slot'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * The design system's button: 40px tall, `radius-md`, a 16/600 label and
 * `shadow-raised` on every variant — nothing in the design is outlined except
 * `outlined` itself, which is the deliberate bordered alternative to a filled
 * primary.
 *
 * Filled actions take `primary-strong`, `danger-strong` and `success-strong`,
 * not the brand hues: white on `primary` is 3.8:1 and fails AA for a 16px
 * label. Hover steps one rung up the tonal ladder on the blue variants only;
 * the design draws no hover for the status variants.
 *
 * Disabled turns `fill` with `text-on-disabled` and **keeps its shadow** — it
 * stays a button that is currently unavailable ("Hinzufügen & Bearbeiten"
 * before a file is chosen), rather than fading out of the page. The `disabled:`
 * utilities win over the variant's own fill because Tailwind emits that
 * modifier last.
 *
 * There is one control height. The Flutter-derived `size` scale and the
 * `outline`/`subtle`/`ghost` variant names it came with are gone; their call
 * sites were migrated with this port.
 */
const buttonStyles = cva(
  'inline-flex h-control items-center justify-center gap-2 whitespace-nowrap rounded-md border border-transparent px-4 font-sans text-body-strong shadow-raised transition-colors disabled:cursor-default disabled:border-transparent disabled:bg-fill disabled:text-text-on-disabled',
  {
    variants: {
      variant: {
        primary: 'bg-primary-strong text-on-primary hover:bg-primary-pressed',
        tonal: 'bg-primary-subtle text-primary-text hover:bg-primary-tonal',
        outlined: 'border-2 border-primary-strong bg-surface text-primary-text hover:bg-primary-subtle',
        danger: 'bg-danger-strong text-on-primary',
        dangerTonal: 'bg-danger-subtle text-danger-text',
        success: 'bg-success-strong text-on-primary',
        successTonal: 'bg-success-subtle text-success-on-subtle',
      },
      block: { true: 'w-full', false: '' },
      /** The `primary` outline a tonal button gains when it is the chosen one. */
      selected: { true: 'border-primary', false: '' },
    },
    defaultVariants: { variant: 'primary', block: false, selected: false },
  },
)

export interface FxButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonStyles> {
  /** Material Symbols name, rendered before the label. */
  icon?: string
  /** Render the caller's own element (a router `<Link>`) with these styles. */
  asChild?: boolean
}

export const FxButton = forwardRef<HTMLButtonElement, FxButtonProps>(
  ({ className, variant, block, selected, icon, asChild = false, children, type, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'
    return (
      <Component
        ref={ref}
        // A button inside a form submits it unless it says otherwise, which is
        // never what a secondary action wants. `asChild` renders someone
        // else's element, which must not be given a button's type.
        type={asChild ? undefined : (type ?? 'button')}
        className={cn(buttonStyles({ variant, block, selected }), className)}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {icon ? <FxIcon name={icon} size={22} /> : null}
            {children}
          </>
        )}
      </Component>
    )
  },
)
FxButton.displayName = 'FxButton'
